import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  type Note,
  type SessionStatus,
  type UploadHandle,
  VOX_SESSION_COMPLETED,
  VoxSdk,
  truncateVoxContext,
} from '@principle-theorem/ai-scribe-sdk';
import {
  OrganisationService,
  PatientSelectorComponent,
  PatientSelectorStore,
} from '@principle-theorem/ng-principle-shared';
import {
  DialogPresets,
  FIREBASE_PROJECT_ID_PROVIDER,
  NgCommonModule,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  ScribePatientContext,
  ScribeSession,
} from '@principle-theorem/principle-core';
import {
  type IMintTokenRequest,
  type IMintTokenResponse,
  type IPatient,
  type IScribeNote,
  type IScribeSession,
  ScribeSessionSource,
  ScribeSessionStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  FileHelpers,
  Firestore,
  type Reffable,
  type SerialisedData,
  type WithRef,
  filterUndefined,
  getFirestoreLink,
  httpsCallable,
  retryWithBackoff,
  serialise,
  snapshot,
  snapshotDefined,
  toISODate,
  toMoment,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { switchMap, take } from 'rxjs/operators';
import {
  type IScribeNoteDialogData,
  ScribeNoteDialogComponent,
} from '../scribe-note-dialog/pr-scribe-note-dialog.component';

const MINT_TOKEN_CALLABLE = 'http-aiScribe-mintToken';

/**
 * Uploads are synchronous, so an `upload` session left `in-progress` for longer
 * than this on load was orphaned by a crash and can be failed.
 */
const STALE_UPLOAD_THRESHOLD_MINUTES = 30;

const NOTE_PERSIST_MAX_ATTEMPTS = 3;

// flat cap; @voxhq/web-sdk 0.0.8 provides no observable upload-completion event
// (progress = local slicing, status = poller-derived) — VoxDenta correspondence,
// Sep 2026; revisit when the vendor exposes one (see cutover runbook).
const UPLOAD_TOTAL_TIMEOUT_MS = 25 * 60 * 1000;

/** Audio extensions the upload tool accepts, matched case-insensitively. */
const ACCEPTED_EXTENSIONS: readonly string[] = ['.mp3', '.wav', '.m4a'];

/** Hard client-side ceiling: files larger than this are rejected before upload. */
const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/** The step the upload flow is currently on, surfaced to the template. */
enum UploadToolStatus {
  Idle = 'idle',
  Minting = 'minting',
  Uploading = 'uploading',
  Processing = 'processing',
  Done = 'done',
  Error = 'error',
}

/** Why a picked file was refused before any upload started. */
export interface IUploadRejection {
  variant: 'type' | 'size';
  fileName: string;
  sizeLabel?: string;
}

/** A picked file reduced to the display fields the template needs. */
export interface IUploadFileView {
  name: string;
  sizeLabel: string;
}

/** A streamed Scribe Note reduced to display fields (status drives spinner→check). */
export interface IUploadNoteView {
  id: string;
  name: string;
  content: string;
  status: Note['status'];
}

/**
 * The single, exhaustive description of what the tool is showing right now — one
 * `kind` per reachable UI state, so the template renders exactly one branch. All
 * arms carry plain display data (no `File`/Firestore refs) so a Storybook story
 * can drive any state by overriding {@link AiScribeUploadToolComponent._buildViewState}.
 */
export type UploadViewState =
  | { kind: 'idle' }
  | { kind: 'file-selected'; file: IUploadFileView }
  | {
      kind: 'uploading';
      file: IUploadFileView;
      progress: number;
      loadedLabel: string;
      preparing: boolean;
    }
  | { kind: 'processing' }
  | { kind: 'streaming'; notes: IUploadNoteView[] }
  | { kind: 'completed'; notes: IUploadNoteView[] }
  | {
      kind: 'failed';
      message: string;
      file?: IUploadFileView;
      progress: number;
    }
  | { kind: 'rejected'; rejection: IUploadRejection };

/**
 * AI Scribe upload test tool. Uploads an audio file via the scribe SDK
 * `uploadFile` path — mint a session token client-side, then let the SDK handle
 * upload, transcription, and note generation. Notes are persisted to the
 * existing `ScribeSession`/`ScribeNote` models. The template renders one of
 * eight explicit {@link UploadViewState} branches.
 *
 * The tool carries no access check of its own: its only host is the management
 * AI Scribe tools page, which is already route-gated.
 */
@Component({
  selector: 'pr-ai-scribe-upload-tool',
  templateUrl: './ai-scribe-upload-tool.component.html',
  styleUrls: ['./ai-scribe-upload-tool.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PatientSelectorStore],
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    PatientSelectorComponent,
  ],
})
export class AiScribeUploadToolComponent {
  private readonly _organisation = inject(OrganisationService);
  private readonly _dialog = inject(MatDialog);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _projectId = inject(FIREBASE_PROJECT_ID_PROVIDER);

  /**
   * The persisted Firestore document for each streamed note, keyed by the
   * vendor's note id. Populated as notes are written so the completed state can
   * open a note without re-querying the session's subcollection.
   */
  private readonly _noteRefs = new Map<
    string,
    DocumentReference<IScribeNote>
  >();

  /**
   * The in-flight SDK upload handle and its 25-minute timeout, tracked on the
   * instance (not just in {@link startUpload}'s closure) so
   * {@link _teardownActiveUpload} can close the handle and clear the timer when
   * the component is destroyed or reset — otherwise both leak past the upload.
   */
  private _activeUploadHandle: UploadHandle | undefined;
  private _activeTimeoutId: ReturnType<typeof setTimeout> | undefined;

  private readonly _status = signal<UploadToolStatus>(UploadToolStatus.Idle);
  private readonly _session = signal<Reffable<IScribeSession> | undefined>(
    undefined
  );
  private readonly _selectedFile = signal<File | undefined>(undefined);
  private readonly _selectedPatient = signal<WithRef<IPatient> | undefined>(
    undefined
  );
  private readonly _progress = signal(0);
  private readonly _loadedBytes = signal(0);
  private readonly _errorMessage = signal<string | undefined>(undefined);
  private readonly _rejection = signal<IUploadRejection | undefined>(undefined);
  private readonly _notes = signal<IUploadNoteView[]>([]);
  private readonly _dragActive = signal(false);

  private readonly _staffer = toSignal(this._organisation.staffer$);

  /** Bound to the reused `pr-patient-selector`; resolves to a `WithRef<IPatient>`
   *  when a patient is chosen, or a raw string while the user is still typing. */
  readonly patientControl = new TypedFormControl<
    WithRef<IPatient> | string | undefined
  >(undefined);
  readonly selectedPatient = this._selectedPatient.asReadonly();
  readonly selectedFile = this._selectedFile.asReadonly();
  readonly status = this._status.asReadonly();
  readonly progress = this._progress.asReadonly();
  readonly errorMessage = this._errorMessage.asReadonly();
  readonly dragActive = this._dragActive.asReadonly();
  /** The status enum, exposed so the template can compare against it. */
  readonly statuses = UploadToolStatus;

  readonly practitionerName = computed(() => this._staffer()?.user.name);

  /**
   * The single view state the template switches on. Derived from the internal
   * signals; kept as a thin call to {@link _buildViewState} so a Storybook
   * subclass can force any state for visual QA.
   */
  readonly viewState = computed(() => this._buildViewState());

  /**
   * Firestore console link to the Scribe *session* document — the one carrying
   * the status/source fields a support engineer needs — or `undefined` until an
   * upload has created a session.
   */
  readonly firestoreLink = computed(() => {
    const session = this._session();
    return session === undefined
      ? undefined
      : getFirestoreLink(this._projectId, session.ref);
  });

  readonly canStart = computed(() => {
    const status = this._status();
    const settled =
      status === UploadToolStatus.Idle ||
      status === UploadToolStatus.Error ||
      status === UploadToolStatus.Done;
    return (
      settled &&
      this._selectedPatient() !== undefined &&
      this._selectedFile() !== undefined
    );
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this._teardownActiveUpload());

    this.patientControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) =>
        this._selectedPatient.set(
          value === undefined || typeof value === 'string' ? undefined : value
        )
      );

    this._organisation.practice$
      .pipe(
        filterUndefined(),
        take(1),
        switchMap((practice) =>
          ScribeSession.byPractice$(practice.ref).pipe(take(1))
        ),
        takeUntilDestroyed()
      )
      .subscribe((sessions) => void this._sweepStaleUploads(sessions));
  }

  /** Records the picked audio file (after validation) and clears any prior error. */
  fileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this._ingestFile(input.files?.[0]);
  }

  /** Accepts a file dropped onto the dropzone through the same validation path. */
  fileDropped(event: DragEvent): void {
    event.preventDefault();
    this._dragActive.set(false);
    this._ingestFile(event.dataTransfer?.files?.[0]);
  }

  /** Highlights the dropzone while a file is dragged over it. */
  dragOver(event: DragEvent): void {
    event.preventDefault();
    this._dragActive.set(true);
  }

  /** Clears the drag highlight when the pointer leaves the dropzone. */
  dragLeave(): void {
    this._dragActive.set(false);
  }

  /** Clears the picked file / rejection, returning the picker to its empty state. */
  clearFile(): void {
    this._selectedFile.set(undefined);
    this._rejection.set(undefined);
    this._errorMessage.set(undefined);
  }

  /** Resets the tool to idle for another upload, keeping the chosen patient. */
  reset(): void {
    this._teardownActiveUpload();
    this._status.set(UploadToolStatus.Idle);
    this._session.set(undefined);
    this._selectedFile.set(undefined);
    this._rejection.set(undefined);
    this._errorMessage.set(undefined);
    this._progress.set(0);
    this._loadedBytes.set(0);
    this._notes.set([]);
    this._noteRefs.clear();
  }

  /**
   * Opens the persisted note in the read-only viewer that lives in this library.
   * Deliberately not the charting review dialog: this surface has no chart
   * context and must never insert (ADR-0004), and reaching into the charting
   * library would close a dependency cycle. A note that never reached Firestore
   * has no ref, so it simply does not open.
   */
  async viewNote(note: IUploadNoteView): Promise<void> {
    const session = this._session();
    const noteRef = this._noteRefs.get(note.id);
    if (session === undefined || noteRef === undefined) {
      return;
    }
    let scribeNote: WithRef<IScribeNote> | undefined;
    try {
      scribeNote = await Firestore.safeGetDoc(noteRef);
    } catch {
      scribeNote = undefined;
    }
    if (scribeNote === undefined) {
      // The note ref is missing or the read failed — surface it rather than
      // opening a dialog on an undefined note that would dereference its fields.
      this._snackBar.open('Unable to open note. Please try again.', 'Dismiss', {
        duration: 6000,
      });
      return;
    }
    this._dialog.open<ScribeNoteDialogComponent, IScribeNoteDialogData>(
      ScribeNoteDialogComponent,
      DialogPresets.medium<IScribeNoteDialogData>({
        data: { session, note: scribeNote },
      })
    );
  }

  /** Opens the upload's Scribe session document in the Firestore console. */
  viewInFirestore(): void {
    const link = this.firestoreLink();
    if (link === undefined) {
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
  }

  /**
   * Runs the full upload pipeline. Mints the session and token first, then hands
   * the file to the scribe SDK, wiring its `progress` / `notes` / `status` /
   * `error` events to the session lifecycle. Any failure fails the session so no
   * orphaned `in-progress` upload session is left behind.
   */
  async startUpload(): Promise<void> {
    const patient = this._selectedPatient();
    const file = this._selectedFile();
    if (patient === undefined || file === undefined) {
      return;
    }
    // Enter Minting synchronously — before any await — so `canStart` flips to
    // false and the now-disabled start button can't mint a second session for
    // the same file on a rapid double click.
    this._status.set(UploadToolStatus.Minting);
    this._errorMessage.set(undefined);
    this._progress.set(0);
    this._loadedBytes.set(0);
    this._notes.set([]);
    this._noteRefs.clear();

    if (file.size === 0) {
      this._fail('The selected file is empty. Choose a valid audio recording.');
      return;
    }

    const staffer = await snapshotDefined(this._organisation.staffer$);
    const practice = await snapshotDefined(this._organisation.practice$);
    const brand = await snapshotDefined(this._organisation.brand$);

    const session = ScribeSession.init({
      stafferRef: staffer.ref,
      practiceRef: practice.ref,
      brandRef: brand.ref,
      source: ScribeSessionSource.Upload,
      recordDate: toISODate(moment()),
    });
    const sessionRef = await ScribeSession.create(patient, session);
    this._session.set({ ...session, ref: sessionRef });

    let tokenResponse: IMintTokenResponse;
    try {
      tokenResponse = await snapshot(
        httpsCallable<SerialisedData<IMintTokenRequest>, IMintTokenResponse>(
          MINT_TOKEN_CALLABLE
        )(
          serialise({
            practiceRef: practice.ref,
            stafferRef: staffer.ref,
            sessionId: sessionRef.id,
          })
        )
      );
    } catch (error) {
      await ScribeSession.markFailed({ ref: sessionRef });
      this._fail(this._messageFromError(error));
      return;
    }

    this._status.set(UploadToolStatus.Uploading);
    let handle: UploadHandle;
    try {
      const factory = VoxSdk.getFactory();
      const sdk = await factory();
      // No `recordedAt` is sent, so VoxDenta stamps the note with the upload
      // instant.
      handle = sdk.uploadFile(
        () => tokenResponse.token,
        ScribeSession.toVoxSessionPath(staffer.ref.id, sessionRef.id),
        file,
        {
          mimeType: file.type || undefined,
          context: truncateVoxContext(ScribePatientContext.build(patient)),
        }
      );
    } catch (error) {
      // Loading the SDK or starting the upload can throw; without this the
      // freshly created session is orphaned `in-progress`. Mirror the mint
      // failure branch: fail the session, surface the error, and tear down.
      await ScribeSession.markFailed({ ref: sessionRef });
      this._fail(this._messageFromError(error));
      this._teardownActiveUpload();
      return;
    }
    this._activeUploadHandle = handle;

    const persistedNoteIds = new Set<string>();

    this._activeTimeoutId = setTimeout(() => {
      void this._failUpload(
        sessionRef,
        `Timed out. The file may still complete on VoxDenta's side — check the session again later.`
      );
    }, UPLOAD_TOTAL_TIMEOUT_MS);

    handle.on('progress', ({ loaded, total }) => {
      if (total > 0) {
        this._progress.set(Math.round((loaded / total) * 100));
        this._loadedBytes.set(loaded);
      }
    });
    handle.on('notes', (notes) =>
      this._notesReceived(sessionRef, notes, persistedNoteIds)
    );
    handle.on('status', (sdkStatus) =>
      this._statusChanged(sessionRef, sdkStatus)
    );
    handle.on('error', (error) => {
      void this._failUpload(sessionRef, this._messageFromError(error));
    });
  }

  /**
   * Builds the {@link UploadViewState} from the internal signals. Protected so a
   * Storybook subclass can override it to force any state without driving the
   * async SDK pipeline.
   */
  protected _buildViewState(): UploadViewState {
    const status = this._status();
    const file = this._selectedFile();

    if (status === UploadToolStatus.Error) {
      return {
        kind: 'failed',
        message: this._errorMessage() ?? DEFAULT_ERROR_MESSAGE,
        file: file ? this._toFileView(file) : undefined,
        progress: this._progress(),
      };
    }
    if (
      status === UploadToolStatus.Minting ||
      status === UploadToolStatus.Uploading
    ) {
      return {
        kind: 'uploading',
        file: file ? this._toFileView(file) : { name: '', sizeLabel: '' },
        progress: this._progress(),
        loadedLabel: FileHelpers.formatBytes(this._loadedBytes()),
        preparing: status === UploadToolStatus.Minting,
      };
    }
    if (status === UploadToolStatus.Processing) {
      const notes = this._notes();
      return notes.length > 0
        ? { kind: 'streaming', notes }
        : { kind: 'processing' };
    }
    if (status === UploadToolStatus.Done) {
      return { kind: 'completed', notes: this._notes() };
    }

    const rejection = this._rejection();
    if (rejection !== undefined) {
      return { kind: 'rejected', rejection };
    }
    if (file !== undefined) {
      return { kind: 'file-selected', file: this._toFileView(file) };
    }
    return { kind: 'idle' };
  }

  /**
   * Notes stream 'generating' → 'ready'; only the final 'ready' emission is
   * persisted, once per note, so partial content never displaces the final note.
   */
  private _notesReceived(
    sessionRef: DocumentReference<IScribeSession>,
    notes: Note[],
    persistedNoteIds: Set<string>
  ): void {
    this._notes.set(notes.map((note) => this._toNoteView(note)));
    const newlyReady = notes.filter(
      (note) => note.status === 'ready' && !persistedNoteIds.has(note.id)
    );
    for (const note of newlyReady) {
      persistedNoteIds.add(note.id);
      void this._persistNote(sessionRef, note);
    }
  }

  private async _persistNote(
    sessionRef: DocumentReference<IScribeSession>,
    note: Note
  ): Promise<void> {
    try {
      await retryWithBackoff(
        async () => {
          const noteRef = await ScribeSession.addNote(
            { ref: sessionRef },
            { content: note.content, voxNoteId: note.id, name: note.name }
          );
          this._noteRefs.set(note.id, noteRef);
        },
        () => true,
        NOTE_PERSIST_MAX_ATTEMPTS
      );
    } catch {
      await this._failUpload(
        sessionRef,
        'Failed to save the generated note. Please try again.'
      );
    }
  }

  private _statusChanged(
    sessionRef: DocumentReference<IScribeSession>,
    sdkStatus: SessionStatus
  ): void {
    if (sdkStatus === 'processing' || sdkStatus === 'writing') {
      this._status.set(UploadToolStatus.Processing);
      return;
    }
    if (sdkStatus === VOX_SESSION_COMPLETED) {
      void this._completeUpload(sessionRef);
    }
  }

  private async _completeUpload(
    sessionRef: DocumentReference<IScribeSession>
  ): Promise<void> {
    try {
      await ScribeSession.markReady({ ref: sessionRef });
      this._session.update((current) =>
        current === undefined
          ? current
          : { ...current, status: ScribeSessionStatus.Ready }
      );
      this._status.set(UploadToolStatus.Done);
    } catch {
      // markReady refusing failed→ready follows an error already surfaced;
      // any other rejection leaves the session un-ready, so never show Done.
      if (this._status() !== UploadToolStatus.Error) {
        this._fail(DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      this._teardownActiveUpload();
    }
  }

  private async _failUpload(
    sessionRef: DocumentReference<IScribeSession>,
    message: string
  ): Promise<void> {
    await ScribeSession.markFailed({ ref: sessionRef });
    this._fail(message);
    this._teardownActiveUpload();
  }

  /** Idempotent: closes the in-flight SDK handle and clears its timeout. */
  private _teardownActiveUpload(): void {
    if (this._activeTimeoutId !== undefined) {
      clearTimeout(this._activeTimeoutId);
      this._activeTimeoutId = undefined;
    }
    if (this._activeUploadHandle !== undefined) {
      this._activeUploadHandle.close();
      this._activeUploadHandle = undefined;
    }
  }

  /**
   * Fails this practice's `upload` sessions still `in-progress` past the stale
   * threshold, orphaned by a crash mid-upload.
   */
  private async _sweepStaleUploads(
    sessions: WithRef<IScribeSession>[]
  ): Promise<void> {
    const cutoff = moment().subtract(STALE_UPLOAD_THRESHOLD_MINUTES, 'minutes');
    const stale = sessions.filter(
      (session) =>
        session.source === ScribeSessionSource.Upload &&
        session.status === ScribeSessionStatus.InProgress &&
        toMoment(session.createdAt).isBefore(cutoff)
    );
    const results = await Promise.allSettled(
      stale.map((session) => ScribeSession.markFailed(session))
    );
    const rejected = results.filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    );
    if (rejected.length > 0) {
      // allSettled so one session failing to close can't strand the rest; this
      // is a fire-and-forget load-time sweep with no user action to surface it
      // against, so log the failures and move on.
      // eslint-disable-next-line no-console
      console.error(
        `AI Scribe: failed to fail ${rejected.length} stale upload session(s)`,
        rejected
      );
    }
  }

  /** Validates a freshly picked file, storing it or the rejection to surface. */
  private _ingestFile(file: File | undefined): void {
    this._errorMessage.set(undefined);
    if (file === undefined) {
      this._selectedFile.set(undefined);
      this._rejection.set(undefined);
      return;
    }
    const rejection = this._validateAudioFile(file);
    if (rejection !== undefined) {
      this._rejection.set(rejection);
      this._selectedFile.set(undefined);
      return;
    }
    this._rejection.set(undefined);
    this._selectedFile.set(file);
  }

  /**
   * Rejects a picked file client-side before any upload: unsupported extension
   * first, then oversize. Returns the rejection to surface, or `undefined`
   * when the file is acceptable.
   */
  private _validateAudioFile(file: File): IUploadRejection | undefined {
    if (!ACCEPTED_EXTENSIONS.includes(FileHelpers.extension(file.name))) {
      return { variant: 'type', fileName: file.name };
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      return {
        variant: 'size',
        fileName: file.name,
        sizeLabel: FileHelpers.formatBytes(file.size),
      };
    }
    return;
  }

  private _toFileView(file: File): IUploadFileView {
    return { name: file.name, sizeLabel: FileHelpers.formatBytes(file.size) };
  }

  private _toNoteView(note: Note): IUploadNoteView {
    return {
      id: note.id,
      name: note.name,
      content: note.content,
      status: note.status,
    };
  }

  private _messageFromError(error: unknown): string {
    if (error instanceof Error && error.message.length > 0) {
      return error.message;
    }
    return DEFAULT_ERROR_MESSAGE;
  }

  private _fail(message: string): void {
    this._errorMessage.set(message);
    this._status.set(UploadToolStatus.Error);
  }
}
